import { z } from "zod";
import { dateSchema, firestoreTimestampSchema } from "./common.schema";

// ============================================
// PATIENT ENTITY SCHEMA
// ============================================

export const patientSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  searchName: z.string(), // Lowercase for autocomplete
  dateOfBirth: dateSchema,
  placeOfBirth: z.string().min(1, "Place of birth is required"),
  jobRoleId: z.string().optional(),
  companyId: z.string().min(1, "Company ID is required"),
  notes: z.string().optional(),
  cf: z.string().optional(), // Codice Fiscale
  geslabId: z.string().optional(),
  gender: z.string().optional(),
  lastVisitAt: firestoreTimestampSchema.optional(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
  email: z.string().email().optional(),

  // Eligibility fields
  eligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(),
  lastVisitId: z.string().optional(),
  eligibilityDate: firestoreTimestampSchema.optional(),
});

export type Patient = z.infer<typeof patientSchema>;

// ============================================
// CREATE PATIENT DTO
// ============================================

export const createPatientSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    dateOfBirth: dateSchema,
    placeOfBirth: z.string().min(1, "Place of birth is required"),
    jobRoleId: z.string().optional(),
    companyId: z.string().optional(),
    notes: z.string().optional(),
    cf: z.string().optional(),
    geslabId: z.string().optional(),
    gender: z.string().optional(),
    email: z.string().email().optional(),

    // Eligibility
    eligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(),
    lastVisitId: z.string().optional(),
    eligibilityDate: dateSchema.optional(),

    // Fields for creating a new company inline
    companyName: z.string().optional(),
    companyAddress: z.string().optional(),
    companyVatNumber: z.string().optional(),
    companyPrimaryDoctorId: z.string().optional(),
    companyCoordinationDoctorIds: z.array(z.string()).optional(),
    companyEmail: z.string().email().optional(),
  })
  .refine((data) => data.companyId || data.companyName, {
    message: "companyId is required when not creating a new company",
    path: ["companyId"],
  });

export type CreatePatient = z.infer<typeof createPatientSchema>;

// ============================================
// UPDATE PATIENT DTO
// ============================================

export const updatePatientSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  dateOfBirth: z.string().optional(),
  placeOfBirth: z.string().optional(),
  jobRoleId: z.string().optional(),
  companyId: z.string().optional(),
  notes: z.string().optional(),
  cf: z.string().optional(),
  geslabId: z.string().optional(),
  gender: z.string().optional(),
  eligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(),
  lastVisitId: z.string().optional(),
  eligibilityDate: dateSchema.optional(),
  email: z.string().email().optional().or(z.string().optional()),
});

export type UpdatePatient = z.infer<typeof updatePatientSchema>;

// ============================================
// UTILITY FUNCTION
// ============================================

export function createPatientSearchName(
  firstName: string,
  lastName: string,
  jobRoleName?: string
): string {
  return `${firstName} ${lastName} ${jobRoleName ?? ""}`.toLowerCase().trim();
}
